import { useForm } from "react-hook-form";
import Button from "../../components/ui/Button";
import SocialAuthButtons from "../../components/SocialAuthButtons";
import { fieldErrorClassName, inputClassName, labelClassName } from "./signupShared";

interface FormValues {
  name: string;
  company: string;
  mobilePhone: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Props {
  defaultEmail?: string;
  onComplete: () => Promise<void>;
}

export default ({ defaultEmail = "", onComplete }: Props) => {
  const { register, handleSubmit, watch, setError, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: {
      name: "",
      company: "",
      mobilePhone: "",
      email: defaultEmail,
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  const onSubmit = handleSubmit(async values => {
    if (values.password !== values.confirmPassword) {
      setError("confirmPassword", { type: "validate", message: "Passwords do not match." });
      return;
    }

    const response = await fetch("/api/auth/password/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: values.name,
        email: values.email,
        password: values.password,
        company: values.company,
        mobilePhone: values.mobilePhone,
      }),
    });
    const result = await response.json().catch(() => null) as { error?: string; field?: keyof FormValues } | null;

    if (!response.ok) {
      const field = result?.field && result.field in errors ? result.field : "email";
      setError(field as keyof FormValues, { type: "server", message: result?.error ?? "Unable to create account." });
      return;
    }

    await onComplete();
  });

  return (
    <div className="space-y-6">
      <SocialAuthButtons returnTo="/signup" disabled={isSubmitting} />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs font-medium uppercase tracking-wide text-slate-400">
          <span className="bg-white px-3">Or create account with email</span>
        </div>
      </div>

      <form className="space-y-4" onSubmit={event => void onSubmit(event)}>
        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-name">Full Name</label>
          <input id="signup-name" type="text" autoComplete="name" placeholder="Enter your full name" className={inputClassName} {...register("name", { required: "Full name is required." })} />
          {errors.name && <p className={fieldErrorClassName}>{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-company">Company</label>
          <input id="signup-company" type="text" autoComplete="organization" placeholder="Enter your company name" className={inputClassName} {...register("company")} />
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-phone">Mobile Phone</label>
          <input id="signup-phone" type="tel" autoComplete="tel" placeholder="Enter your mobile phone" className={inputClassName} {...register("mobilePhone")} />
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-email">Email Address</label>
          <input id="signup-email" type="email" autoComplete="email" placeholder="Enter your email" className={inputClassName} {...register("email", { required: "Email is required." })} />
          {errors.email && <p className={fieldErrorClassName}>{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-password">Password</label>
          <input id="signup-password" type="password" autoComplete="new-password" placeholder="Create a password (min. 12 characters)" className={inputClassName} {...register("password", { required: "Password is required.", minLength: { value: 12, message: "Password must be at least 12 characters." } })} />
          {errors.password && <p className={fieldErrorClassName}>{errors.password.message}</p>}
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="signup-confirm">Confirm Password</label>
          <input
            id="signup-confirm"
            type="password"
            autoComplete="new-password"
            placeholder="Confirm your password"
            className={inputClassName}
            {...register("confirmPassword", {
              required: "Please confirm your password.",
              validate: value => value === password || "Passwords do not match.",
            })}
          />
          {errors.confirmPassword && <p className={fieldErrorClassName}>{errors.confirmPassword.message}</p>}
        </div>

        <Button type="submit" className="h-12 w-full" disabled={isSubmitting}>
          {isSubmitting ? "Creating account..." : "Continue to Accounting"}
        </Button>
      </form>
    </div>
  );
};
